import React from 'react';
import styled from 'styled-components';
import { convertHexToRGBA, logger } from '@utils/usefulFuncs';
import { SkeletonButton, SkeletonButtonWrap } from '@ui';
import { Body5, Container } from '@ui';
import ErrorText from '@ui/ErrorText';
import { FileUploader } from 'react-drag-drop-files';
import { getFileExtensionType, stripFileExtension } from '@utils/usefulFuncs';
import { CloseButton } from '@ui/buttons/CloseButton';

const DropZoneWrapper = styled.div`
  label {
    border: none;
  }

  svg {
    height: 36px;
    width: 36px;
    fill: ${({ theme }) => theme.themeColors.primaryCtaButton};

    * {
      fill: ${({ theme }) => theme.themeColors.primaryCtaButton};
    }
  }

  * {
    color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  }
`;

const DropZoneBody = styled.div`
  position: relative;
  border: 2px dashed ${({ theme }) => theme.themeColors.primaryCtaButton};
  border-radius: 5px;
  min-height: 20vh;
  width: 100%;
  max-width: unset;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;

  ${({ $isDragging, theme }) =>
    $isDragging ? `background-color: ${convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.1)}` : ``}
`;

const ErrorIconWrapper = styled.div`
  svg {
    height: 36px;
    width: 36px;
    fill: ${({ theme }) => theme.themeColors.error};

    * {
      fill: ${({ theme }) => theme.themeColors.error};
    }
  }

  ${SkeletonButtonWrap} {
    padding: 0;

    button {
      padding: 4px 16px;
    }
  }
`;

const FileEntryWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 4px;
`;

const FileNameInput = styled.input`
  font-size: 14px;
  line-height: 18px;
  font-family: inherit;
  padding: 2px 4px;
  border-radius: 3px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.themeColors.text};
  background: transparent;
  outline: none;
  min-width: 80px;
  width: 100%;
  border: none;
  cursor: text;
`;

const CloseButtonWrapper = styled.div`
  margin-left: auto;

  svg {
    height: 10px;
    width: 10px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;

const FileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ height: 16, width: 16 }}>
    <path d="M14 2H6C4.89 2 4 2.9 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2M18 20H6V4H13V9H18V20Z" />
  </svg>
);

const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M23 18H20V15H18V18H15V20H18V23H20V20H23M6 2C4.89 2 4 2.9 4 4V20C4 21.11 4.89 22 6 22H13.81C13.45 21.38 13.2 20.7 13.08 20H6V4H13V9H18V13.08C18.33 13.03 18.67 13 19 13C19.34 13 19.67 13.03 20 13.08V8L14 2M8 12V14H16V12M8 16V18H13V16Z" />
  </svg>
);

const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M20 17H22V15H20V17M20 7V13H22V7M4 2C2.89 2 2 2.89 2 4V20C2 21.11 2.89 22 4 22H16C17.11 22 18 21.11 18 20V8L12 2M4 4H11V9H16V20H4Z" />
  </svg>
);

function buildFileTypes(allowedTypes) {
  if (!allowedTypes || allowedTypes.length === 0) {
    logger('No allowed types in drag and drop, drop disabled', { allowedTypes });
    return [];
  }
  const typeMap = {
    pdf: ['PDF'],
    doc: ['DOC'],
    docx: ['DOC', 'DOCX'],
    xls: ['XLS', 'XLSX', 'XLSM', 'XLSB'],
    xlsx: ['XLSX', 'XLSM', 'XLSB'],
    csv: ['CSV'],
    ppt: ['PPT'],
    pptx: ['PPT', 'PPTX'],
    txt: ['TXT'],
  };
  const set = new Set();
  allowedTypes.forEach((t) => {
    const mapped = typeMap[t.toLowerCase()];
    if (mapped) {
      mapped.forEach((m) => set.add(m));
    } else {
      set.add(t.toUpperCase());
    }
  });
  return [...set];
}

function FileEntry({ file, onRemove, onRename }) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(file.name);
  const inputRef = React.useRef(null);
  const isCommitting = React.useRef(false);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const commitRename = () => {
    if (isCommitting.current) return;
    isCommitting.current = true;

    const trimmed = draft.trim();
    if (trimmed && trimmed !== file.name && onRename) {
      onRename(file, trimmed);
    } else {
      setDraft(file.name);
    }
    setIsEditing(false);
    isCommitting.current = false;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setDraft(file.name);
      setIsEditing(false);
    }
  };

  return (
    <FileEntryWrapper>
      <FileIcon />
      <FileNameInput
        ref={inputRef}
        $isEditing={isEditing}
        readOnly={!isEditing}
        value={isEditing ? draft : `${file.name}.${file.type}`}
        onClick={() => !isEditing && setIsEditing(true)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitRename}
        onKeyDown={isEditing ? handleKeyDown : undefined}
      />
      <CloseButtonWrapper>
        <CloseButton onClick={() => onRemove(file)} />
      </CloseButtonWrapper>
    </FileEntryWrapper>
  );
}

export function FileDragAndDrop({ files, onAddFiles, onRemoveFile, onRenameFile, allowedTypes, multiple = false }) {
  const [fileTypes] = React.useState(() => buildFileTypes(allowedTypes));
  const [isDragging, setIsDragging] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleTypeError = () => {
    setError('File type is not supported, only ' + fileTypes.map((t) => '.' + t.toLowerCase()).join(', ') + ' files.');
  };

  const handleChange = (fileOrFiles) => {
    setError(null);
    try {
      const incoming = [];
      if (fileOrFiles instanceof FileList || Array.isArray(fileOrFiles)) {
        for (let i = 0; i < fileOrFiles.length; i++) {
          const file = fileOrFiles[i];
          incoming.push({
            name: stripFileExtension(file),
            type: getFileExtensionType(file),
            file,
          });
        }
      } else if (fileOrFiles) {
        incoming.push({
          name: stripFileExtension(fileOrFiles),
          type: getFileExtensionType(fileOrFiles),
          file: fileOrFiles,
        });
      }
      onAddFiles(incoming);
    } catch (e) {
      console.error('FileDragAndDrop handleChange error:', e);
    }
  };

  return (
    <div>
      <DropZoneWrapper>
        <FileUploader
          onDraggingStateChange={setIsDragging}
          handleChange={handleChange}
          onTypeError={handleTypeError}
          name="file"
          hoverTitle={''}
          dropMessageStyle={{ display: 'none' }}
          disabled={!!error}
          types={fileTypes}
          multiple={multiple}
          fileOrFiles={null}
        >
          {error ? (
            <DropZoneBody>
              <ErrorIconWrapper>
                <Container fullWidth all={24} row verticallyCenter wrap>
                  <ErrorIcon />
                  <Container left={16}>
                    <ErrorText>{error}</ErrorText>
                  </Container>
                  <Container left={16}>
                    <SkeletonButton onClick={() => setError(null)}>Clear</SkeletonButton>
                  </Container>
                </Container>
              </ErrorIconWrapper>
            </DropZoneBody>
          ) : (
            <DropZoneBody $isDragging={isDragging}>
              <Container fullWidth all={24} row verticallyCenter wrap>
                <Container right={16}>
                  <UploadIcon />
                </Container>
                {isDragging ? (
                  <Body5>Drop here</Body5>
                ) : (
                  <Body5>Drag and drop files, or click to open upload window.</Body5>
                )}
              </Container>
            </DropZoneBody>
          )}
        </FileUploader>
      </DropZoneWrapper>

      {files.length > 0 && (
        <Container top={12} column>
          {files.map((f, i) => (
            <Container key={`${f.name}-${i}`} bottom={8}>
              <FileEntry file={f} onRemove={onRemoveFile} onRename={onRenameFile} />
            </Container>
          ))}
        </Container>
      )}
    </div>
  );
}
